import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/dialog';
import { Drawer } from '@/components/ui/drawer';
import { Button } from '@repo/ui/components/button';
import { CategorySelectionStep } from './category-selection-step';
import { ProductSelectionStep } from './product-selection-step';
import { QuantitySelectionStep } from './quantity-selection-step';
import { ProductCategory, Product } from '@repo/shared-types';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '../form/input';
import { useIsMobile } from '@repo/ui/hooks/use-mobile';

interface AddProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: ProductCategory[];
  products: Product[];
  playerId: string | null;
  playerName?: string;
  onCreateSaleItem: (
    productIds: string[],
    playerId: string | null,
    playerName?: string,
    price?: number
  ) => Promise<void>;
}

type WizardStep = 'category' | 'product' | 'quantity';

export function AddProductModal({
  open,
  onOpenChange,
  categories,
  products,
  playerId,
  playerName,
  onCreateSaleItem,
}: AddProductModalProps) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<WizardStep>('category');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [search, setSearch] = useState('');

  // Filter categories that have products
  const categoriesWithProducts = useMemo(() => {
    return categories.filter((category) => {
      const categoryProducts = products.filter((p) => p.category?.id === category.id);
      return categoryProducts.length > 0;
    });
  }, [categories, products]);

  // Get products for selected category
  const categoryProducts = useMemo(() => {
    if (!selectedCategoryId) return [];
    return products.filter((p) => p.category?.id === selectedCategoryId);
  }, [products, selectedCategoryId]);

  // Get selected product
  const selectedProduct = useMemo(() => {
    if (!selectedProductId) return null;
    return products.find((p) => p.id === selectedProductId) || null;
  }, [products, selectedProductId]);

  const filteredProducts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return products.filter((p) => p.name?.toLowerCase().includes(q));
  }, [products, search]);

  // Reset wizard when modal closes
  useEffect(() => {
    if (!open) {
      setStep('category');
      setSelectedCategoryId(null);
      setSelectedProductId(null);
      setSearch('');
    }
  }, [open]);

  const handleSelectCategory = (categoryId: string) => {
    setSearch('');
    setSelectedCategoryId(categoryId);
    setStep('product');
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProductId(productId);
    setStep('quantity');
  };

  const handleConfirmQuantity = async (quantity: number, price?: number) => {
    if (!selectedProductId) return;

    setIsCreating(true);
    try {
      // Create array with productId repeated quantity times
      const productIds = Array(quantity).fill(selectedProductId);
      await onCreateSaleItem(productIds, playerId, playerName, price);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating sale item:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleBack = () => {
    if (step === 'product') {
      setStep('category');
      setSelectedCategoryId(null);
    } else if (step === 'quantity') {
      setStep('product');
      setSelectedProductId(null);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      onOpenChange(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'category':
        return 'Selecciona la categoría';
      case 'product':
        return 'Selecciona el producto';
      case 'quantity':
        return 'Cantidad';
      default:
        return 'Agregar producto';
    }
  };

  const renderContent = () => {
    const backButton = (
      <Button
        variant="outline"
        onClick={handleBack}
        disabled={isCreating}
        className={cn(step === 'category' ? 'opacity-0 pointer-events-none' : '', 'mr-4')}
      >
        <ChevronLeft className="size-4" />
        Anterior
      </Button>
    );

    return (
      <div className="flex flex-col h-full">
        {isMobile ? (
          <div className="flex flex-row items-center justify-between gap-4 shrink-0 pb-4">
            <h2 className="text-lg font-semibold">{getStepTitle()}</h2>
            {backButton}
          </div>
        ) : (
          <DialogHeader className="flex flex-row items-center justify-between gap-4 shrink-0 pb-4">
            <DialogTitle>{getStepTitle()}</DialogTitle>
            {backButton}
          </DialogHeader>
        )}

        <div className="flex-1 min-h-0 overflow-auto">
          {step === 'category' && (
            <>
              <div className="mb-4">
                <Input
                  placeholder="Buscar producto..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {search.trim() ? (
                <ProductSelectionStep
                  products={filteredProducts}
                  onSelectProduct={handleSelectProduct}
                />
              ) : (
                <CategorySelectionStep
                  categories={categoriesWithProducts}
                  onSelectCategory={handleSelectCategory}
                />
              )}
            </>
          )}

          {step === 'product' && (
            <ProductSelectionStep
              products={categoryProducts}
              onSelectProduct={handleSelectProduct}
            />
          )}

          {step === 'quantity' && selectedProduct && (
            <div className="h-full overflow-y-auto">
              <QuantitySelectionStep
                productName={selectedProduct.name}
                defaultPrice={selectedProduct.price}
                onConfirm={handleConfirmQuantity}
              />
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-4 border-t shrink-0">
          <Button variant="secondary" onClick={handleClose} disabled={isCreating}>
            Cancelar
          </Button>
        </div>
      </div>
    );
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleClose}>
        <div className="pb-4 max-h-[90vh] overflow-y-auto">{renderContent()}</div>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-[1200px]! h-[85vh] flex flex-col">
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
